import React, { useState } from 'react';
import { Package, AlertCircle } from 'lucide-react';
import { Task, Dumpster } from '../../types';
import SearchableSelect from '../SearchableSelect';

interface DumpsterSelectionModalProps {
  task: Task;
  dumpsters: Dumpster[];
  onConfirm: (dumpsterId: string) => void;
  onCancel: () => void;
}

const DumpsterSelectionModal: React.FC<DumpsterSelectionModalProps> = ({ 
  task, 
  dumpsters, 
  onConfirm, 
  onCancel 
}) => {
  const [selectedDumpsterId, setSelectedDumpsterId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDumpsterId) {
      alert('Veuillez sélectionner une benne');
      return;
    }

    onConfirm(selectedDumpsterId);
  };

  // Filter out inactive dumpsters and the unknown dumpster
  const availableDumpsters = dumpsters.filter(dumpster => 
    dumpster.isActive !== false && 
    dumpster.name !== 'Benne Inconnue' &&
    dumpster.number !== 'UNKNOWN'
  );

  const dumpsterOptions = availableDumpsters.map(dumpster => ({
    value: dumpster.id,
    label: `${dumpster.name} (${dumpster.number})`
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-2 text-blue-600 mb-4">
          <Package className="h-6 w-6" />
          <h2 className="text-xl font-bold">Sélectionner une benne</h2>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-700 font-medium">Benne requise</p>
              <p className="text-sm text-blue-600 mt-1">
                Cette tâche nécessite qu'une benne spécifique soit assignée avant de pouvoir commencer.
                Veuillez sélectionner la benne que vous allez utiliser.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Benne à utiliser
            </label>
            <SearchableSelect
              options={dumpsterOptions}
              value={selectedDumpsterId}
              onChange={setSelectedDumpsterId}
              placeholder="Sélectionner une benne..."
              searchPlaceholder="Rechercher par nom ou numéro..."
            />
            
            {availableDumpsters.length === 0 && (
              <p className="text-sm text-red-600 mt-2">
                Aucune benne disponible. Veuillez contacter l'administrateur.
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            
            <button
              type="submit"
              disabled={!selectedDumpsterId || availableDumpsters.length === 0}
              className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirmer et démarrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DumpsterSelectionModal;